/* eslint-disable react-refresh/only-export-components -- co-located exports are intentional */
import { Component, ReactNode, ErrorInfo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/common/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Translated fallback UI for ErrorBoundary
 * Extracted as a functional component to use the useTranslation hook
 */
function ErrorBoundaryFallback({
  error,
  errorInfo,
  onReset,
  onReload,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation('errors');

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base px-4">
      <div className="max-w-md w-full bg-surface-raised rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
          <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>

        <h2 className="text-2xl font-bold text-content-primary text-center mb-2">
          {t('boundary.title')}
        </h2>

        <p className="text-content-secondary text-center mb-6">
          {t('boundary.description')}
        </p>

        {error && (
          <details className="mb-6 text-sm">
            <summary className="cursor-pointer text-content-secondary font-medium mb-2">
              {t('boundary.errorDetails')}
            </summary>
            <div className="bg-surface-base rounded p-4 overflow-auto max-h-64">
              <p className="font-mono text-xs text-red-600 dark:text-red-400 break-words">
                {error.toString()}
              </p>
              {errorInfo && (
                <pre className="font-mono text-xs text-content-secondary mt-2 whitespace-pre-wrap">
                  {errorInfo.componentStack}
                </pre>
              )}
            </div>
          </details>
        )}

        <div className="flex gap-3">
          <Button
            onClick={onReset}
            variant="outline"
            className="flex-1"
          >
            {t('boundary.tryAgain')}
          </Button>
          <Button
            onClick={onReload}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t('boundary.reloadPage')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Error Boundary component to catch and display errors gracefully
 * Uses a class component for error catching (required by React)
 * with a functional child component for translated UI
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI - delegated to functional component for i18n hook access
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}
